import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub } from "@fortawesome/free-brands-svg-icons";
import {
  faStar,
  faDollarSign,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import "./Project.css";

interface ProjectInfo {
  name: string;
  description: string;
  githubUrl: string;
  stars?: number;       // optional star count
  investment?: string;  // e.g. "$2.5M" or "500K"
  conference?: string;  // e.g. "GPTDao 2024"
  star_detail?: string;  // optional details
  investment_detail?: string;  // optional details
  conference_detail?: string;  // optional details
}

// Example data with optional fields:
const projects: ProjectInfo[] = [
    {
    name: "GotHub",
    description:
      "Essentially GPT Store + Devin, but before GPT Store came out. Submit a request and AI Agent will write your code and dev containers, return a github pull link for you.",
    githubUrl: "https://github.com/wayne-wang-1119/Gothub",
    investment: "310K",
    conference: "MoleDAO 2023",
    investment_detail: "300K Term Sheet from MiraclePlus for the 2024 Spring Batch",
    conference_detail: "10K from MoleDAO 2023 First place for the AI Hackathon",
  },
  {
    name: "ClipSift: AI Video Digest",
    description:
      "ClipSift helps you get best video assets for your vlog / meme / etc. By providing a natural lanaguage description, you can easily query the top relevant YouTube videos and get the most relevant clips to review.",
    githubUrl:
      "https://github.com/wayne-wang-1119/ClipSift-AI-Powered-Video-Digest/",
    stars: 40,
    star_detail: "40 stars in 2 weeks after release. Contender for the Backdrop V3 Race Hackathon",
  },
  {
    name: "CktGym",
    description:
      "Decentralized solution for training circuits and optimizing designs",
    githubUrl:
      "https://github.com/BWRC-AMS-ML-Discovery/BwrcAmsMlDiscovery",
    conference: "Berkeley Data Science Discovery Fair 2023",
    conference_detail: "Demoed at the Berkeley Data Science Discovery Fair 2023. Poster: https://docs.google.com/presentation/d/1M-PLXy3W7eWZ7BPLXuiTjOk7UZ5pRz2j/edit?usp=sharing&ouid=112791325536752981711&rtpof=true&sd=true",
  },
  {
    name: "Generative-CNN-for-Candlestick-Images",
    description:
      "Use DC-GAN and CNN Inception V3 image pattern recognition for candlestick image classification and generation, massively reduce the training size needed for vision models application to Quant.",
    githubUrl:
      "https://github.com/wayne-wang-1119/Generative-CNN-for-Candlestick-Images",
      conference: "ICAIF 2024",
      conference_detail: "First author accepted to ICAIF 2024, Contender for the Most Innovative Paper Award, ranked top 10 of all submissions",
  },
  {
    name: "Reunion",
    description:
      "Few-shot clone human voice and chat history to preserve your loved ones forever",
    githubUrl: "https://github.com/wayne-wang-1119/Reunion",
  },
  {
    name: "FindMoney",
    description:
      "A Flutter application to help established businesses find others like them, invested by Angels for small amount",
    githubUrl: "https://github.com/wayne-wang-1119/findmoney",
    investment: "20K",
    investment_detail: "20K from Angels",
  },
  {
    name: "Pirates of the Caribbean",
    description:
      "CS61B Game in Java, find the treasure box in the dungeon. What can I say I'm a huge fan of Johnny Depp.",
    githubUrl:
      "https://github.com/wayne-wang-1119/Pirates-of-The-Caribbean",
  },
  {
    name: "County Housing Data Analysis",
    description:
      "Classic Data 100 project, analyzing housing data in Alameda County",
    githubUrl:
      "https://github.com/wayne-wang-1119/CCAO-project-data-analysis-data100",
  },
];

const Project: React.FC = () => {
  return (
    <div className="project-container">
      <h1 className="project-heading text-glow">Projects</h1>
      <div className="project-grid">
        {projects.map((project, index) => (
          <div key={index} className="project-card">
            {/* Top-right icons/labels if (stars || investment || conference) exist */}
            {(project.stars || project.investment || project.conference) && (
              <div className="project-badges">
                {project.stars !== undefined && (
                  <BadgeIcon
                    icon={faStar}
                    shortLabel={String(project.stars)}
                    tooltip={`GitHub: ${project.stars} stars\nLink: ${project.star_detail}`}
                  />
                )}
                {project.investment && (
                  <BadgeIcon
                    icon={faDollarSign}
                    shortLabel={project.investment}
                    tooltip={`Investment details: ${project.investment_detail}`}
                  />
                )}
                {project.conference && (
                  <BadgeIcon
                    icon={faUsers}
                    shortLabel={project.conference}
                    tooltip={`Conference details: ${project.conference_detail}`}
                  />
                )}
              </div>
            )}

            {/* Card content */}
            <h2 className="project-title">{project.name}</h2>
            <p className="project-description">{project.description}</p>

            <a
              href={project.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="github-button"
            >
              <FontAwesomeIcon icon={faGithub} className="github-icon" />
              View on GitHub
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

interface BadgeIconProps {
  icon: any;
  shortLabel: string; // the text displayed next to the icon (e.g. "123" or "$2.1M")
  tooltip: string;    // the text displayed in the hover tooltip
}

/**
 * A small helper component for each top-right "badge."
 * Displays an icon + short label, and on hover, shows a tooltip with details.
 */
const BadgeIcon: React.FC<BadgeIconProps> = ({ icon, shortLabel, tooltip }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="badge-icon-wrapper"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <FontAwesomeIcon icon={icon} className="badge-icon" />
      <span className="badge-label">{shortLabel}</span>

      {/* Tooltip (shown on hover) */}
      {hovered && <div className="badge-tooltip">{tooltip}</div>}
    </div>
  );
};

export default Project;
